import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { walletAPI } from '../services/api';
import PageShell from '../components/PageShell';
import {
  Money, PiggyBank, Receipt, Briefcase,
  Plus, PaperPlaneTilt, Wallet as WalletIcon, ClockCounterClockwise,
} from '@phosphor-icons/react';

function formatCurrency(amount) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
}

function BalanceCard({ label, icon: Icon, amount, max, testId }) {
  const { t } = useTheme();
  const pct = Math.min(100, Math.max(0, max ? (amount / max) * 100 : 0));
  return (
    <div
      className="rounded-2xl px-4 py-4 flex items-center gap-3 transition-all duration-200"
      style={{
        background: t.surface,
        border: `1px solid ${t.borderSubtle}`,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = t.borderHover;
        e.currentTarget.style.boxShadow = t.hoverGlow;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = t.borderSubtle;
        e.currentTarget.style.boxShadow = 'none';
      }}
      data-testid={testId}
    >
      <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: t.accentSoft }}>
        <Icon className="w-5 h-5" weight="bold" style={{ color: t.accent }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-xs font-medium tracking-tight truncate uppercase" style={{ color: t.textMutedNew }}>{label}</span>
          <span className="text-base font-bold tabular-nums" style={{ fontFamily: 'Cabinet Grotesk', color: t.textPrimary }}>{formatCurrency(amount)}</span>
        </div>
        <div className="h-1 rounded-full mt-2 overflow-hidden" style={{ background: t.dividerBorder }}>
          <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: t.accent }} />
        </div>
      </div>
    </div>
  );
}

function ActivityButton({ to, label, icon: Icon, testId }) {
  const { t } = useTheme();
  return (
    <Link
      to={to}
      data-testid={testId}
      className="group rounded-2xl px-4 py-6 flex flex-col items-center justify-center gap-2 transition-all duration-200"
      style={{
        background: t.surface,
        border: `1px solid ${t.borderSubtle}`,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = t.borderHover;
        e.currentTarget.style.boxShadow = t.hoverGlow;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = t.borderSubtle;
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <div className="w-11 h-11 rounded-xl flex items-center justify-center transition-transform group-hover:scale-105" style={{ background: t.accentSoft }}>
        <Icon className="w-5 h-5" weight="bold" style={{ color: t.accent }} />
      </div>
      <span className="text-sm font-semibold tracking-tight" style={{ color: t.textPrimary }}>{label}</span>
    </Link>
  );
}

export default function WalletPage() {
  const { user } = useAuth();
  const { t } = useTheme();
  const [, setBalance] = useState(0);

  useEffect(() => {
    walletAPI.getBalance()
      .then((res) => setBalance(res.data.balance || 0))
      .catch((err) => console.error('Wallet: failed to load balance:', err));
  }, []);

  const firstName = user?.name?.split(' ')[0] || user?.username || 'there';
  const cash = user?.wallet_balance || 0;

  const balances = [
    { label: 'Cash',     icon: Money,     amount: cash, max: 5000, testId: 'balance-cash' },
    { label: 'Savings',  icon: PiggyBank, amount: 0,    max: 5000, testId: 'balance-savings' },
    { label: 'Sales',    icon: Receipt,   amount: 0,    max: 5000, testId: 'balance-sales' },
    { label: 'Holdings', icon: Briefcase, amount: 0,    max: 5000, testId: 'balance-holdings' },
  ];

  return (
    <PageShell testId="balance-page">
      <div className="mb-6" data-testid="wallet-hero-header">
        <p className="text-[11px] tracking-[0.18em] uppercase font-semibold mb-2" style={{ color: t.textFaint }}>Wallet</p>
        <h1 className="text-3xl sm:text-4xl font-black tracking-tight leading-tight" style={{ fontFamily: 'Cabinet Grotesk', color: t.textPrimary }}>
          Hi, {firstName}. Here's your balance.
        </h1>
        <p className="text-sm mt-1.5" style={{ color: t.textMutedNew }}>Cash flow, sales and holdings across your Divispend workspace.</p>
      </div>

      <section className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8" data-testid="balance-grid">
        {balances.map((b) => <BalanceCard key={b.label} {...b} />)}
      </section>

      <h2 className="text-[11px] tracking-[0.16em] uppercase font-bold mb-3 px-1" style={{ color: t.accentText }}>Activity</h2>
      <div className="grid grid-cols-3 gap-3 mb-6" data-testid="activity-grid">
        <ActivityButton to="/wallet/topup" label="Add Money" icon={Plus} testId="activity-topup" />
        <ActivityButton to="/send"         label="Send"      icon={PaperPlaneTilt} testId="activity-send" />
        <ActivityButton to="/history"      label="History"   icon={WalletIcon} testId="activity-history" />
      </div>

      <Link
        to="/history"
        className="inline-flex items-center gap-2 text-xs transition-colors hover:opacity-80"
        style={{ color: t.textMutedNew }}
        data-testid="wallet-view-history"
      >
        <ClockCounterClockwise className="w-4 h-4" />
        View full transaction history
      </Link>
    </PageShell>
  );
}
